import { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CircularProgress,
  Switch,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { useSnackbar } from "notistack";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ResizableDataGrid from "components/ResizableDataGrid";

function Banners() {
  const auth = localStorage.getItem("authToken");
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bannerImageModal, setBannerImageModal] = useState({
    open: false,
    path: null,
  });

  const columns = [
    {
      field: "banner_id",
      headerName: "ID",
      width: isSmall ? 70 : isMedium ? 90 : 100,
      minWidth: isSmall ? 60 : 80,
    },
    {
      field: "path",
      headerName: "Image",
      width: isSmall ? 180 : isMedium ? 230 : 280,
      minWidth: isSmall ? 140 : 190,
      renderCell: (params) => (
        <MDBox
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <MDBox
            component="img"
            src={`https://pancham-bucket.s3.amazonaws.com/${params.value}`}
            onClick={(e) => {
              e.stopPropagation();
              setBannerImageModal({ open: true, path: params.value });
            }}
            sx={{
              width: isSmall ? 112 : isMedium ? 132 : 156,
              height: isSmall ? 62 : isMedium ? 74 : 88,
              objectFit: "cover",
              backgroundColor: "#f8f9fa",
              borderRadius: "md",
              boxShadow: theme.shadows[1],
              border: "1px solid",
              borderColor: "grey.300",
              cursor: "pointer",
            }}
          />
        </MDBox>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: isSmall ? 145 : isMedium ? 165 : 170,
      minWidth: isSmall ? 120 : 140,
      renderCell: (params) => (
        <MDBox display="flex" alignItems="center" gap={0.75}>
          <Switch
            checked={Boolean(params.value)}
            onChange={() =>
              handleStatusToggle(params.row.banner_id, !params.value)
            }
          />
          <Chip
            size="small"
            label={params.value ? "Active" : "Inactive"}
            color={params.value ? "success" : "default"}
            variant={params.value ? "filled" : "outlined"}
          />
        </MDBox>
      ),
    },
    {
      field: "updated_at",
      headerName: "Last Updated",
      width: isSmall ? 170 : isMedium ? 205 : 235,
      minWidth: isSmall ? 150 : 180,
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: isSmall ? 205 : isMedium ? 230 : 250,
      minWidth: isSmall ? 160 : 190,
      renderCell: (params) => (
        <MDBox
          display="flex"
          gap={0.75}
          flexDirection={isSmall ? "column" : "row"}
          alignItems={isSmall ? "stretch" : "center"}
        >
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => handleUpdateImageClick(params.row.banner_id)}
            sx={{
              width: isSmall ? "100%" : "auto",
              minWidth: isSmall ? 0 : 96,
              px: 1,
              py: 0.6,
              fontSize: "0.65rem",
            }}
          >
            Update Image
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            size="small"
            onClick={() => handleDelete(params.row.banner_id, params.row.path)}
            sx={{
              width: isSmall ? "100%" : "auto",
              minWidth: isSmall ? 0 : 66,
              px: 1,
              py: 0.6,
              fontSize: "0.65rem",
            }}
          >
            Delete
          </MDButton>
        </MDBox>
      ),
    },
  ];

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/banner/getall`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      if (res.data.success) {
        const sortedBanners = [...(res.data.data || [])].sort(
          (a, b) => Number(a.banner_id) - Number(b.banner_id)
        );
        setBanners(sortedBanners);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error fetching banners", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (bannerId, newStatus) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/banner/update-status`,
        { banner_id: bannerId, status: newStatus },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Status updated", { variant: "success" });
        fetchBanners();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating status", { variant: "error" });
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleAddBanner = async () => {
    if (!selectedFile) {
      enqueueSnackbar("Please select an image", { variant: "warning" });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("image", selectedFile);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/banner/add`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      if (res.data.success) {
        enqueueSnackbar("Banner added successfully", { variant: "success" });
        setSelectedFile(null);
        setShowAdd(false);
        fetchBanners();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error adding banner", { variant: "error" });
    } finally {
      setUploading(false);
    }
  };

  const [updatingBannerId, setUpdatingBannerId] = useState(null);
  
  const handleUpdateImageClick = (id) => {
    setUpdatingBannerId(id);
    document.getElementById("update-image-input").click();
  };

  const handleUpdateImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !updatingBannerId) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("image", file);
    formData.append("banner_id", updatingBannerId);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/banner/update-image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      if (res.data.success) {
        enqueueSnackbar("Image updated", { variant: "success" });
        fetchBanners();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error updating image", { variant: "error" });
    } finally {
      setUploading(false);
      setUpdatingBannerId(null);
    }
  };

  const handleDelete = async (id, path) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) return;
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/banner/delete`,
        { banner_id: id, path },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      if (res.data.success) {
        enqueueSnackbar("Banner deleted", { variant: "success" });
        fetchBanners();
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error deleting banner", { variant: "error" });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems={isSmall ? "flex-start" : "center"}
                flexDirection={isSmall ? "column" : "row"}
                gap={isSmall ? 2 : 0}
              >
                <MDTypography variant="h6" color="white">
                  Banners Management
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="white"
                  onClick={() => setShowAdd(!showAdd)}
                >
                  {showAdd ? "Cancel" : "Add New Banner"}
                </MDButton>
              </MDBox>
              
              {showAdd && (
                <MDBox p={3} display="flex" flexDirection="column" gap={2}>
                  <MDTypography variant="h6">Upload New Banner Image</MDTypography>
                  <input type="file" accept="image/*" onChange={handleFileChange} />
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={handleAddBanner}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Save Banner"}
                  </MDButton>
                </MDBox>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="inherit" />
                  </MDBox>
                ) : (
                  <MDBox sx={{ height: isSmall ? 640 : 540 }} p={2}>
                    <ResizableDataGrid
                      getRowId={(row) => row.banner_id}
                      rows={banners}
                      columns={columns}
                      rowHeight={isSmall ? 118 : isMedium ? 106 : 98}
                      initialState={{
                        pagination: {
                          paginationModel: {
                            pageSize: 10,
                          },
                        },
                      }}
                      sx={{
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#f8f9fa",
                        },
                        "& .MuiDataGrid-cell": {
                          display: "flex",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                          fontWeight: 700,
                        },
                        "& .MuiDataGrid-row:hover": {
                          backgroundColor: "#fafcff",
                        },
                      }}
                      disableRowSelectionOnClick
                    />
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={bannerImageModal.open}
        onClose={() => setBannerImageModal({ open: false, path: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Banner Preview
          <IconButton
            aria-label="close"
            onClick={() => setBannerImageModal({ open: false, path: null })}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (themeArg) => themeArg.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {bannerImageModal.path && (
            <MDBox
              component="img"
              src={`https://pancham-bucket.s3.amazonaws.com/${bannerImageModal.path}`}
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: "lg",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <input
        type="file"
        id="update-image-input"
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleUpdateImage}
      />
    </DashboardLayout>
  );
}

export default Banners;
